import React, { useRef } from 'react';
import * as S from './PaintPicker.style';
import { motion } from 'framer-motion';
import {
    Image,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { FloatingAction } from '@modules/paint-picker/component/floating-action/FloatingAction';
import { PaintPalette } from '@modules/paint-picker/component/paint-colours/PaintPalette';
import { MobilePaintPalette } from '@modules/paint-picker/component/paint-colours/MobilePaintPalette';
import { Resene } from '@archipro-design/icons';
import { fabAnimation } from '@modules/paint-picker/component/floating-action/FloatingAction.style';
import {
    paletteAnimation,
    mobilePreviewAnimation,
} from '@modules/paint-picker/component/paint-colours/PaintPalette.style';
import { RESENE_LOGO } from '@modules/paint-picker/config';
import { usePaintPickerContext } from '@modules/paint-picker/context/PaintPickerContext';

import { useConfigContext } from '@archipro-website/config/bindings/react';
import {
    getReseneLogoAdEventData,
    useAdEventHandler,
} from '@archipro-website/tracker';

interface PaintPickerProps {
    bannerUrl?: string;
}

export const PaintPicker = ({ bannerUrl }: PaintPickerProps) => {
    const { desktop } = useAppDisplayModeContext();
    const config = useConfigContext();
    const {
        state: { previewPaint, paintPickerActive },
        dispatch,
        closePaintPicker,
        paintSelection,
        openPaintPicker,
    } = usePaintPickerContext();
    const { css } = useStyles({ desktop });
    const reseneAd = getReseneLogoAdEventData();
    const reseneAdRef = useRef(null);
    const { onClick: onReseneLogoClick } = useAdEventHandler(
        reseneAd,
        reseneAdRef
    );

    const logo = (
        <Image
            ref={reseneAdRef}
            src={`${config.assetsBaseUrl}${RESENE_LOGO}`}
            alt={'Resene'}
            width={86}
            height={24}
            layout={'intrinsic'}
        />
    );

    const openPaintPalette = () => {
        if (paintSelection.length === 0) {
            if (bannerUrl) {
                // set banner image as active image to draw random colours from initially
                dispatch({ type: 'SET_ACTIVE_IMAGE', payload: bannerUrl });
            }
        }
        openPaintPicker();
    };

    return (
        <motion.div
            initial={false}
            className={css(S.PaintPicker)}
            animate={paintPickerActive ? 'open' : 'closed'}
        >
            {desktop && (
                <motion.div variants={fabAnimation}>
                    <FloatingAction
                        onClick={() => {
                            openPaintPalette();
                            onReseneLogoClick('Ad_CTAClick');
                        }}
                        icon={<Resene />}
                        tooltipText={'Colour selector'}
                        tooltipImage={logo}
                    />
                </motion.div>
            )}

            {desktop ? (
                <motion.div variants={paletteAnimation}>
                    <PaintPalette
                        icon={<Resene />}
                        logo={logo}
                        onClose={() => closePaintPicker()}
                    />
                </motion.div>
            ) : (
                <motion.div variants={mobilePreviewAnimation}>
                    {previewPaint && <MobilePaintPalette {...previewPaint} />}
                </motion.div>
            )}
        </motion.div>
    );
};
